import { AlignmentType, Document, Header as DocXHeader, WidthType } from "docx";
import moment from "moment";
import { DateFormat } from "../../../../../../constants";
import { formatDate } from "../useFormatDateTime";
import { breakLine, C, Header, line, table, text } from "./common";

type TripBusiness = {
  BusinessUnit: string | undefined;
  from: string;
  des: string;
  to: string;
  date: string;
};

const getTravelPaperForm = (item: any) => {
  const idHead = item[0].ITHead;

  return new Document({
    sections: item.map((e: any) => {
      const [StartDate, EndDate] = e?.BusinessTime?.split("-");
      const {
        fullYear: startFullYear,
        monthOfYear: startMonthOfYear,
        dayOfMonth: startDayOfMonth,
      } = formatDate(StartDate);

      const { fullYear: endFullYear, monthOfYear: endMonthOfYear, dayOfMonth: endDayOfMonth } = formatDate(EndDate);

      const travelArray: TripBusiness[] = e.TripBusiness.slice(0, -1).map(
        (currentElement: TripBusiness, i: number) => ({
          from: currentElement.from,
          des: e.TripBusiness.length === 1 ? e.TripBusiness[i].to : e.TripBusiness[i + 1].from,
          to: currentElement.to,
          date: currentElement.date,
        }),
      );

      const renderTrips = (item: TripBusiness) => {
        return text(
          [
            { text: `Nơi đi: ${item.from}`, size: 22 },
            { text: `Nơi đến: ${item.to}`, break: 1, size: 22 },
          ],
          { alignment: AlignmentType.LEFT },
        );
      };

      const renderTime = (item: TripBusiness, index: number) => {
        return text(
          [
            { text: `Từ: ${moment(item.date).format(DateFormat.DD_MM_YYYY)}`, size: 22 },
            { text: `Đến: ../../${moment(item.date).year()}`, size: 22, break: 1 },
          ],
          { alignment: AlignmentType.LEFT },
        );
      };

      const getApprover = (steps: any[]) => {
        if (!steps.length) return "";

        const lastStep = steps[steps.length - 1];

        if (!lastStep.length) return "";

        const approver = lastStep.find((approver: any) => approver.isApproved) || lastStep[0];

        const approverName = approver.name;

        return approverName;
      };

      return {
        headers: {
          default: new DocXHeader({
            children: [
              C.row(
                C.col(undefined, [
                  text(
                    [
                      { text: "TCT HÀNG KHÔNG VN", size: 22 },
                      {
                        text: "CÔNG TY DVMĐ SÂN BAY VIỆT NAM",
                        size: 22,
                        break: 1,
                        bold: true,
                      },
                    ],
                    { alignment: AlignmentType.CENTER },
                  ),
                  line(800),
                ]),
                C.col(undefined, Header.rightParagraph),
              ),
            ],
          }),
        },
        children: [
          C.row(
            C.col(50, [
              text("Số:            /GĐĐ-VIAGS", {
                alignment: AlignmentType.CENTER,
              }),
            ]),
            C.col(50),
          ),

          breakLine(1),

          text({ text: "GIẤY ĐI ĐƯỜNG", bold: true, size: 28 }, { alignment: AlignmentType.CENTER }),

          text({ text: `Cấp cho ông/bà: ${e.PassportForPerson || ""}`, size: 22 }),

          text({ text: `Chức vụ: ${e.Position || ""}`, size: 22 }),

          text({ text: `Đơn vị: ${e.Unit || ""}`, size: 22 }),

          text({ text: `Được cử đi công tác tại: ${e.SendOnForBusiness || ""}`, size: 22 }),

          text({
            text: `Từ ngày ${startDayOfMonth} tháng ${startMonthOfYear} năm ${startFullYear} đến ngày ${endDayOfMonth} tháng ${endMonthOfYear} năm ${endFullYear}`,
            size: 22,
          }),

          text({
            text: `(theo công văn số ${e.eBaseOnDecision ? e.eBaseOnDecision + "" : ""})`,
            size: 22,
            italics: true,
          }),

          breakLine(1),

          C.row(
            C.col(50),
            C.col(50, [
              text(
                [
                  {
                    text: `Tp. Hồ Chí Minh, ngày    tháng ${startMonthOfYear} năm ${startFullYear}`,
                    italics: true,
                    size: 22,
                  },
                  {
                    text: idHead.split("|")[0] === "hieptd@viags.vn" ? "CHÁNH VĂN PHÒNG CÔNG TY" : "NGƯỜI DUYỆT",
                    bold: true,
                    size: 22,
                    break: 1,
                  },
                  {
                    text: idHead.split("|")[0] === "hieptd@viags.vn" ? idHead.split("|")[1] : getApprover(e.eSteps),
                    bold: true,
                    break: 4,
                    size: 22,
                  },
                ],
                { alignment: AlignmentType.CENTER },
              ),
            ]),
          ),

          breakLine(2),

          table<{
            BusinessUnit: string | undefined;
            from: string;
            des: string;
            to: string;
            date: string;
          }>({
            columns: [
              {
                label: {
                  text: "Nơi đi – Nơi đến",
                  size: 22,
                },
                renderText: renderTrips,
              },
              {
                label: {
                  text: "Từ ngày – Đến ngày",
                  size: 22,
                },
                renderText: renderTime,
              },
              {
                label: {
                  text: "Đơn vị công tác",
                  size: 22,
                },
                renderText: (item) =>
                  text(
                    {
                      text: item.to,
                      size: 22,
                    },
                    { alignment: AlignmentType.LEFT },
                  ),
              },
              {
                label: {
                  text: "Nội dung công tác",
                  size: 22,
                },
                renderText: (item) =>
                  text(
                    {
                      text: e.BusinessContent,
                    },
                    { alignment: AlignmentType.LEFT },
                  ),
              },
              {
                label: {
                  text: "Xác nhận của đơn vị công tác (Ký tên, đóng dấu)",
                  size: 22,
                },
                renderText: (item) => text(""),
              },
              {
                label: {
                  text: "Xác nhận của CB-NV sử dụng xe (*)",
                  size: 22,
                },
                renderText: (item) => text(""),
              },
            ],
            data: travelArray ?? [],
          }),

          text({
            text: "Lưu ý (*): CB-NV sử dụng xe ô tô ký xác nhận giấy công của NV lái xe",
            italics: true,
            size: 22,
          }),

          breakLine(1),

          text({ text: "THANH TOÁN", bold: true, size: 22 }, { alignment: AlignmentType.CENTER }),

          breakLine(1),

          C.row(
            C.col(
              {
                width: { size: 40, type: WidthType.PERCENTAGE },
                margins: { top: 100, bottom: 100 },
              },
              [
                text({ text: "Thủ trưởng cơ quan/đơn vị", bold: true, size: 24 }),
                text({ text: "cử cán bộ đi công tác duyệt:", bold: true, size: 24 }),
                text({
                  text: "Thời gian công tác theo yêu cầu công việc và được hưởng phụ cấp là …… ngày …. đêm",
                  size: 24,
                }),
                text({ text: "Số ngày trùng với ngày đi công tác: …….", size: 22 }),
                text({ text: "(Ký, họ tên)", italics: true, size: 22 }, { alignment: AlignmentType.CENTER }),
              ],
            ),
            C.col(
              {
                width: { size: 60, type: WidthType.PERCENTAGE },
                margins: { top: 100, bottom: 100, left: 200, right: 200 },
              },
              [
                text({
                  text: "Cán bộ, nhân viên đi công tác đề nghị thanh toán:",
                  bold: true,
                  size: 22,
                }),
                text({ text: "- Phụ cấp lưu trú: ……………………………………", size: 22 }),
                text({ text: "- Trừ tiền ăn ca do chi trùng ngày công tác: ……..…", size: 22 }),
                text({ text: "- Tiền đi lại khoán: …………………………..……", size: 22 }),
                text({ text: "- Tiền tàu xe/lệ phí vé/taxi: Phiếu🔲, Khoán: .....", size: 22 }),
                text({ text: "- Tiền ở: Khách sạn 🔲 Khoán: ..…………….……", size: 22 }),
                text({
                  text: "- Chi khác: ..............................................................",
                  size: 22,
                }),
                text({
                  text: "- Tổng cộng: ...........................................................",
                  size: 22,
                }),
                text({ text: "(Ký, họ tên)", italics: true, size: 22 }, { alignment: AlignmentType.CENTER }),
              ],
            ),
          ),
        ],
      };
    }),
  });
};

export default getTravelPaperForm;
